import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, UserPlus, KeyRound, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export const ROLE_LABELS = { admin: "Yönetici", cutter: "Kesimci", shipper: "Sevkiyatçı" };
const ROLE_HINT = { cutter: "Sadece Kesim ekranını görür, yeni kesim girer", shipper: "Sadece Sevkiyat ekranını görür, yeni sevkiyat girer" };
const empty = { name: "", email: "", password: "", role: "cutter" };
const inputCls = "h-11 bg-[#EFE6D6] border-[#DCCFB8]";

export default function Staff() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/users", form); toast.success("Personel hesabı açıldı"); setForm(empty); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`${u.name} için yeni şifre (en az 8 karakter):`);
    if (!pw) return;
    try { await api.put(`/users/${u.id}/password`, { password: pw }); toast.success("Şifre değiştirildi"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`${u.name} hesabı silinsin mi? Bu kişi artık giriş yapamaz.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Hesap silindi"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-5 lg:p-10 max-w-[1100px]" data-testid="staff-page">
      <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Hesaplar</p>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight flex items-center gap-3">
        <Users className="w-7 h-7 text-[#D97706]" /> Personel
      </h1>
      <p className="text-[#6B5B4A] mt-2 text-sm mb-8">Personel sadece kendi ekranını görür; yeni kayıt girebilir ama düzenleyemez veya silemez.</p>

      <section className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5 mb-6" data-testid="staff-create-card">
        <h2 className="font-display text-lg font-bold uppercase tracking-tight mb-3">Yeni Personel Hesabı</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Input placeholder="Ad Soyad *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" className={inputCls} />
          <Input type="email" placeholder="E-posta *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" className={inputCls} />
          <Input type="text" placeholder="Şifre * (en az 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" className={inputCls} />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="staff-role" className={inputCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118]">
              <SelectItem value="cutter" data-testid="staff-role-cutter">Kesimci</SelectItem>
              <SelectItem value="shipper" data-testid="staff-role-shipper">Sevkiyatçı</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-[#8C7B68]">{ROLE_HINT[form.role]}</p>
          <Button onClick={create} data-testid="staff-create-btn" className="h-10 bg-[#D97706] hover:bg-[#B45309] text-white">
            <UserPlus className="w-4 h-4 mr-1" /> Hesap Aç</Button>
        </div>
      </section>

      <div className="space-y-2" data-testid="staff-list">
        {users.map((u) => (
          <div key={u.id || u.email} className="flex items-center justify-between rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] px-4 py-3" data-testid={`staff-row-${u.email}`}>
            <div className="min-w-0">
              <p className="font-semibold truncate flex items-center gap-2">
                {u.role === "admin" && <ShieldCheck className="w-4 h-4 text-[#B45309]" />}{u.name || "—"}
              </p>
              <p className="text-xs text-[#6B5B4A] truncate">{u.email}</p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <span className="text-[11px] font-mono uppercase px-2 py-1 rounded border border-[#DCCFB8] bg-[#EFE6D6]" data-testid={`staff-role-badge-${u.email}`}>{ROLE_LABELS[u.role]}</span>
              {u.role !== "admin" && <>
                <button onClick={() => resetPw(u)} title="Şifre değiştir" data-testid={`staff-reset-${u.email}`} className="text-[#8C7B68] hover:text-[#B45309]"><KeyRound className="w-4 h-4" /></button>
                <button onClick={() => remove(u)} title="Sil" data-testid={`staff-delete-${u.email}`} className="text-[#8C7B68] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
              </>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
